/**
 * Resolves when the given media query matches, or when it changes to match.
 * @param {Object} options
 * @param {string} options.query - The media query string to match against.
 * @returns {Promise<boolean>} Resolves to true when the media query matches.
 */
function media({ query }) {
  const mediaQuery = globalThis.matchMedia(query);
  return new Promise(function (resolve) {
    if (mediaQuery.matches) {
      resolve(true);
    } else {
      mediaQuery.addEventListener('change', resolve, { once: true });
    }
  });
}

/**
 * Resolves when the given element becomes visible in the viewport.
 * @param {Object} options
 * @param {Element} options.element - The DOM element to observe for visibility.
 * @returns {Promise<boolean>} Resolves to true when the element is visible.
 */
function visible({ element }) {
  return new Promise(function (resolve) {
    const observer = new globalThis.IntersectionObserver(async function (
      entries
    ) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.disconnect();
          resolve(true);
          break;
        }
      }
    });
    observer.observe(element);
  });
}

/**
 * Resolves when the browser is idle (using requestIdleCallback or a timeout fallback).
 * @returns {Promise<void>} Resolves when the browser is idle.
 */
function idle() {
  return new Promise(function (resolve) {
    if ('requestIdleCallback' in globalThis) {
      globalThis.requestIdleCallback(resolve);
    } else {
      setTimeout(resolve, 200);
    }
  });
}

/**
 * An object mapping module paths to dynamic import functions for all islands.
 * @type {Object.<string, Function>}
 */
export const islands = import.meta.glob('/modules/**/*.js');

/**
 * Hydrates custom elements ("islands") on the page based on their attributes and conditions.
 * Observes the DOM for new elements and hydrates them as needed.
 *
 * @param {Object.<string, Function>} islands - An object mapping module paths to dynamic import functions.
 * @returns {void}
 */
export function revive(islands) {
  const observer = new globalThis.MutationObserver(mutations => {
    for (const { addedNodes } of mutations) {
      for (const node of addedNodes) {
        if (node.nodeType === 1) dfs(node);
      }
    }
  });

  async function dfs(node) {
    const tagName = node.tagName.toLowerCase();
    const potentialJsPath = `/modules/${tagName}/${tagName}.js`;
    const isPotentialCustomElementName = /-/.test(tagName);

    let child = node.firstElementChild;

    while (child) {
      dfs(child);
      child = child.nextElementSibling;
    }

    if (isPotentialCustomElementName && islands[potentialJsPath]) {
      if (node.hasAttribute('client:visible')) {
        await visible({ element: node });
      }

      const clientMedia = node.getAttribute('client:media');
      if (clientMedia) {
        await media({ query: clientMedia });
      }

      if (node.hasAttribute('client:idle')) {
        await idle();
      }

      islands[potentialJsPath]();
    }
  }

  dfs(document.body);

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}
